import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Alert, AlertStatus, AlertType } from 'src/types';
import { formatLocalTimestamp } from 'src/utils/timestamps';

import Modal from 'src/UtilComponents/Modal';
import './AlarmProtocol.css';
import { useAuth } from 'src/Contexts/AuthContext';
import { useSelectedInstallation } from 'src/Contexts/SelectedInstallationContext';
import { useToast } from 'src/Contexts/ToastContext';
import {
  useAlertsQuery,
  useAcknowledgeAlertMutation,
  useResolveAlertMutation,
  useReopenAlertMutation,
  useDeleteAlertMutation,
  useInstallationsQuery,
  usePolygonsQuery,
} from 'src/queries';
import ConfirmDialog from 'src/Pages/Management/components/ConfirmDialog';

import MapWithAlerts from 'src/Components/Visualize2D/MapWithAlerts';

const ALL_TYPES: AlertType[] = ['leakage', 'power', 'offline', 'stale'];
const ALL_STATUSES: AlertStatus[] = ['active', 'acknowledged', 'resolved'];

function deriveStatus(alert: Alert): AlertStatus {
  if (alert.resolved_at) return 'resolved';
  if (alert.acknowledged_at) return 'acknowledged';
  return 'active';
}

const AlarmProtocol = () => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { currentUser } = useAuth();
  const isAdmin = Boolean(currentUser?.is_admin);
  const { selectedInstallationID } = useSelectedInstallation();
  const { data: installations = [] } = useInstallationsQuery();
  const installationName = useMemo(() => {
    const map = new Map(installations.map((i: any) => [i.id, i.name]));
    return (id: string) => map.get(id) ?? id.slice(0, 8);
  }, [installations]);

  const [typeFilter, setTypeFilter] = useState<AlertType[]>(ALL_TYPES);
  const [statusFilter, setStatusFilter] = useState<AlertStatus[]>(ALL_STATUSES);

  const {
    data: alerts = [],
    isError: alertsError,
    isLoading: alertsLoading,
  } = useAlertsQuery(
    {
      installation_id: selectedInstallationID,
      type: typeFilter.length === ALL_TYPES.length ? undefined : typeFilter,
      status: statusFilter.length === ALL_STATUSES.length ? undefined : statusFilter,
    },
    true
  );

  const { data: polygons = [], isError: polygonsError } = usePolygonsQuery(selectedInstallationID);

  const ackMutation = useAcknowledgeAlertMutation();
  const resolveMutation = useResolveAlertMutation();
  const reopenMutation = useReopenAlertMutation();
  const deleteMutation = useDeleteAlertMutation();

  const [selected, setSelected] = useState<Alert | null>(null);
  const closeModal = () => setSelected(null);

  // Pending hard-delete confirmation. Set when the user clicks Delete on a
  // row; cleared on confirm or cancel.
  const [pendingDelete, setPendingDelete] = useState<Alert | null>(null);
  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
    });
  };

  useEffect(() => {
    if (alertsError) toast.error(t('Failed to load alerts'));
    if (polygonsError) toast.error(t('Failed to load installation structure'));
  }, [alertsError, polygonsError, toast, t]);

  const dateFormat: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };

  const toggle = <T,>(set: T[], value: T): T[] =>
    set.includes(value) ? set.filter((s) => s !== value) : [...set, value];

  const renderStatusBadge = (status: AlertStatus) => (
    <span className={`alarm-status alarm-status--${status}`}>{t(`alarm.status.${status}`)}</span>
  );

  // Whether the alert carries any spatial context we can render — either a
  // focus point or measurements from the snapshot at alert time. We don't
  // replay the measurements as MPLE markers/contours (too brittle without
  // the original interpolated grid), but their presence signals "show the
  // roof outline" so the user gets at least the structural context.
  const hasSpatialContext =
    !!selected?.focus_point || (selected?.measurements && selected.measurements.length > 0);

  return (
    <div className="alarmprotocol-page">
      <div className="alarm-filters">
        <div className="alarm-filter-group">
          <span className="alarm-filter-label">{t('alarm.filter.type')}:</span>
          {ALL_TYPES.map((typ) => (
            <label key={typ} className="alarm-filter-option">
              <input
                type="checkbox"
                checked={typeFilter.includes(typ)}
                onChange={() => setTypeFilter((s) => toggle(s, typ))}
              />
              {t(`alarm.type.${typ}`)}
            </label>
          ))}
        </div>
        <div className="alarm-filter-group">
          <span className="alarm-filter-label">{t('alarm.filter.status')}:</span>
          {ALL_STATUSES.map((s) => (
            <label key={s} className="alarm-filter-option">
              <input
                type="checkbox"
                checked={statusFilter.includes(s)}
                onChange={() => setStatusFilter((sel) => toggle(sel, s))}
              />
              {t(`alarm.status.${s}`)}
            </label>
          ))}
        </div>
      </div>

      <div className="alarm-table-container">
        <table className="alarm-table">
          <thead>
            <tr>
              <th>{t('alarm.col.timestamp')}</th>
              <th>{t('alarm.col.installation')}</th>
              <th>{t('alarm.col.type')}</th>
              <th>{t('alarm.col.cabinet')}</th>
              <th>{t('alarm.col.focusPoint')}</th>
              <th>{t('alarm.col.status')}</th>
              <th>{t('alarm.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {alertsLoading && (
              <tr>
                <td colSpan={7} className="alarm-table-empty">
                  {t('Loading...')}
                </td>
              </tr>
            )}
            {!alertsLoading && alerts.length === 0 && (
              <tr>
                <td colSpan={7} className="alarm-table-empty">
                  {t('alarm.noAlerts')}
                </td>
              </tr>
            )}
            {alerts.map((a) => {
              const status = deriveStatus(a);
              const fp = a.focus_point;
              return (
                <tr key={a.id} className="alarm-row" onClick={() => setSelected(a)}>
                  <td>{formatLocalTimestamp(a.timestamp, i18n.language, dateFormat)}</td>
                  <td>{installationName(a.installation_id)}</td>
                  <td>{t(`alarm.type.${a.type}`)}</td>
                  <td>{a.schaltschranknummer ?? '—'}</td>
                  <td>{fp ? `(${fp.x.toFixed(2)}, ${fp.y.toFixed(2)})` : '—'}</td>
                  <td>{renderStatusBadge(status)}</td>
                  <td className="alarm-actions" onClick={(e) => e.stopPropagation()}>
                    {status === 'active' && (
                      <>
                        <button
                          className="alarm-btn alarm-btn--ack"
                          disabled={ackMutation.isPending}
                          onClick={() => ackMutation.mutate(a.id)}
                        >
                          {t('alarm.action.acknowledge')}
                        </button>
                        <button
                          className="alarm-btn alarm-btn--resolve"
                          disabled={resolveMutation.isPending}
                          onClick={() => resolveMutation.mutate(a.id)}
                        >
                          {t('alarm.action.resolve')}
                        </button>
                      </>
                    )}
                    {status === 'acknowledged' && (
                      <button
                        className="alarm-btn alarm-btn--resolve"
                        disabled={resolveMutation.isPending}
                        onClick={() => resolveMutation.mutate(a.id)}
                      >
                        {t('alarm.action.resolve')}
                      </button>
                    )}
                    {status === 'resolved' && (
                      <button
                        className="alarm-btn alarm-btn--reopen"
                        disabled={reopenMutation.isPending}
                        onClick={() => reopenMutation.mutate(a.id)}
                      >
                        {t('alarm.action.reopen')}
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        className="alarm-btn alarm-btn--delete"
                        disabled={deleteMutation.isPending}
                        onClick={() => setPendingDelete(a)}
                      >
                        {t('alarm.action.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        title={t('alarm.confirmDeleteTitle')}
        message={t('alarm.confirmDeleteMessage')}
        isLoading={deleteMutation.isPending}
      />

      {selected && (
        <Modal onClose={closeModal}>
          <div>
            <h3>
              {t(`alarm.type.${selected.type}`)} —{' '}
              {formatLocalTimestamp(selected.timestamp, i18n.language, dateFormat)}
            </h3>
            {selected.message && <p>{selected.message}</p>}
            {selected.focus_point && (
              <p>
                {t('alarm.col.focusPoint')}: ({selected.focus_point.x.toFixed(2)},{' '}
                {selected.focus_point.y.toFixed(2)})
              </p>
            )}
          </div>
          {hasSpatialContext ? (
            // We deliberately don't replay the historical MPLE points or
            // contour heatmap — the snapshot can't be reconstructed cleanly
            // from the stored row, and a half-rendered field is more
            // confusing than no field at all. Polygons + a focus marker
            // tell the user "where on the roof", which is all this view needs.
            <MapWithAlerts
              points={[]}
              polygons={polygons}
              focusPoint={selected.focus_point ?? null}
            />
          ) : (
            <p className="alarm-modal-empty">{t('alarm.noMeasurements')}</p>
          )}
        </Modal>
      )}
    </div>
  );
};

export default AlarmProtocol;
